import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, ShieldAlert, ShieldX, Zap } from "lucide-react";
import { toast } from "sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { Toaster } from "./components/ui/sonner";
import { Card, CardContent } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { MintScanner } from "./components/MintScanner";
import { SafetyScoreGauge } from "./components/SafetyScoreGauge";
import { RiskCheckCard } from "./components/RiskCheckCard";
import { TokenHeader } from "./components/TokenHeader";
import { HoldersTable } from "./components/HoldersTable";
import { LiquidityPanel } from "./components/LiquidityPanel";
import { DeployerCard } from "./components/DeployerCard";
import { RecentScansDrawer } from "./components/RecentScansDrawer";
import { ResultsSkeleton } from "./components/ResultsSkeleton";
import { BundleAnalysisCard } from "./components/BundleAnalysisCard";
import { HoneypotCard } from "./components/HoneypotCard";
import { VolumeAuthenticityCard } from "./components/VolumeAuthenticityCard";
import { BcvsCard } from "./components/BcvsCard";
import { CeberCard } from "./components/CeberCard";
import { MsisCard } from "./components/MsisCard";
import { CmssCard } from "./components/CmssCard";
import { LiquidityBleedDashboard } from "./components/LiquidityBleedDashboard";
import { PositionsPanel } from "./components/PositionsPanel";
import { RwaSignals } from "./components/RwaSignals";
import { scanToken } from "./lib/api";
import { loadRecent, saveRecent, clearRecent } from "./lib/recent";
import { chainLabel } from "./lib/chainValidation";

function TopBar({ onOpenRecents, recentCount, chain }) {
  return (
    <header className="sticky top-0 z-30 backdrop-blur bg-[hsl(var(--bg)/0.75)] border-b border-[hsl(var(--border))]">
      <div className="container mx-auto px-4 sm:px-6 max-w-[1200px] h-14 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 group" data-testid="brand-home">
          <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-md bg-[hsl(var(--bg-elev-2))] border border-[hsl(var(--primary)/0.4)]">
            <Zap size={15} className="text-[hsl(var(--primary))]" />
          </span>
          <span className="font-semibold tracking-tight text-base">
            Rug<span className="text-[hsl(var(--primary))]">Scan</span>
          </span>
          <Badge variant="muted" className="hidden sm:inline-flex">
            {chainLabel(chain || "solana")}
          </Badge>
        </a>
        <button
          type="button"
          onClick={onOpenRecents}
          data-testid="recent-scans-open-button-top"
          className="text-xs text-[hsl(var(--muted))] hover:text-[hsl(var(--fg))] inline-flex items-center gap-1.5"
        >
          Recent {recentCount ? <Badge variant="primary">{recentCount}</Badge> : null}
        </button>
      </div>
    </header>
  );
}

function HeroSummary({ chain }) {
  const isSolana = !chain || chain === "solana";
  return (
    <div className="text-center pt-10 sm:pt-14 pb-6 sm:pb-8">
      <motion.h1
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight"
      >
        {chainLabel(chain || "solana")} Token{" "}
        <span className="text-[hsl(var(--primary))]">Risk Scanner</span>
      </motion.h1>
      <p className="mt-3 text-sm sm:text-base text-[hsl(var(--muted))] max-w-2xl mx-auto">
        {isSolana
          ? "Paste any SPL mint address to inspect on-chain authorities, holder concentration, DEX liquidity, deployer reputation, and a 0–100 safety score."
          : `Paste a ${chainLabel(chain)} contract address to inspect ownership, liquidity, deployer history, and get a 0–100 safety score.`}
      </p>
      {isSolana && (
        <ul className="mt-6 flex flex-wrap items-center justify-center gap-2 text-[11px] uppercase tracking-wider text-[hsl(var(--muted))]">
          {[
            ["mint authority", "success"],
            ["freeze authority", "success"],
            ["metadata mutability", "warning"],
            ["holder concentration", "primary"],
            ["dex liquidity", "primary"],
            ["deployer reputation", "warning"],
          ].map(([t, v]) => (
            <li key={t}>
              <Badge variant={v}>{t}</Badge>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WarningsBanner({ warnings }) {
  if (!warnings || !warnings.length) return null;
  return (
    <Card className="bg-[hsl(var(--warning)/0.06)] border-[hsl(var(--warning)/0.4)]">
      <CardContent className="py-3">
        <div className="flex items-start gap-2 text-[12px] text-[hsl(var(--warning))]">
          <ShieldAlert size={14} className="mt-0.5 shrink-0" />
          <div className="space-y-0.5">
            <div className="font-semibold">Partial data</div>
            <ul className="text-[11px] text-[hsl(var(--muted))] list-disc pl-4">
              {warnings.slice(0, 3).map((w, i) => (
                <li key={i} className="break-all">{String(w)}</li>
              ))}
              {warnings.length > 3 ? (
                <li>+{warnings.length - 3} more</li>
              ) : null}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Footer({ chain }) {
  const isSolana = !chain || chain === "solana";
  return (
    <footer className="mt-16 border-t border-[hsl(var(--border))]">
      <div className="container mx-auto px-4 sm:px-6 max-w-[1200px] py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-[hsl(var(--muted))]">
        <span>
          RugScan is informational only. The score is heuristic — it is NOT
          financial or legal advice. Always do your own research.
        </span>
        <span className="font-mono">
          {isSolana
            ? "solana mainnet · public RPC + Helius · DexScreener · Jupiter v2"
            : `${chainLabel(chain)} · Blockscout · Uniswap V2/V3/V4 · DexScreener`}
        </span>
      </div>
    </footer>
  );
}

export default function App() {
  const [chain, setChain] = React.useState("solana");
  const [scanning, setScanning] = React.useState(false);
  const [report, setReport] = React.useState(null);
  const [error, setError] = React.useState("");
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [recent, setRecent] = React.useState(() => loadRecent());

  // Read ?mint= / ?token= + ?chain= on first load
  React.useEffect(() => {
    const url = new URL(window.location.href);
    const token = url.searchParams.get("token") || url.searchParams.get("mint");
    const chainParam = url.searchParams.get("chain");
    if (chainParam) setChain(chainParam);
    if (token) {
      handleScan(token, chainParam || "solana", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScan = async (token, scanChain, pushUrl = true) => {
    const activeChain = scanChain || chain;
    if (!token) return;
    setScanning(true);
    setError("");
    setReport(null);
    if (pushUrl) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set("token", token);
        url.searchParams.set("chain", activeChain);
        window.history.replaceState({}, "", url.toString());
      } catch {}
    }
    try {
      const data = await scanToken(token, activeChain);
      setReport(data);
      const jp = data?.jupiter || {};
      const md = data?.metadata || {};
      saveRecent({
        mint: data.mint || data.token || token,
        chain: activeChain,
        name: jp.name || md.name || data?.token_meta?.name || "",
        symbol: jp.symbol || md.symbol || data?.token_meta?.symbol || "",
        logo: jp.icon || "",
        score: data?.risk?.score ?? null,
        label: data?.risk?.label ?? "",
      });
      setRecent(loadRecent());
      toast.success(
        `Scan complete · ${data?.risk?.label ?? ""} · ${data?.risk?.score ?? 0}/100`
      );
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Scan failed. Please try again.";
      setError(String(typeof msg === "object" ? JSON.stringify(msg) : msg));
      toast.error("Scan failed", {
        description: String(typeof msg === "object" ? JSON.stringify(msg) : msg),
      });
    } finally {
      setScanning(false);
    }
  };

  const handleClearRecent = () => {
    clearRecent();
    setRecent([]);
    toast.success("Recent scans cleared");
  };

  const hasReport = !!(report?.mint || report?.token);
  const checks = report?.risk?.checks || [];
  const isSolana = chain === "solana";

  return (
    <TooltipProvider delayDuration={200}>
      <div className="min-h-screen relative">
        <TopBar
          onOpenRecents={() => setDrawerOpen(true)}
          recentCount={recent.length}
          chain={chain}
        />
        <main className="container mx-auto px-4 sm:px-6 max-w-[1200px] relative z-10">
          {!hasReport && !scanning ? <HeroSummary chain={chain} /> : null}

          <section className={`${hasReport || scanning ? "pt-6" : "pt-2"} pb-4`}>
            <LiquidityBleedDashboard />
          </section>

          <section className="pb-4">
            <PositionsPanel />
          </section>

          <section
            className={`${hasReport || scanning ? "pt-2 pb-6" : "pb-10"}`}
          >
            <Card className="p-4 sm:p-5">
              <MintScanner
                scanning={scanning}
                onScan={(token) => handleScan(token, chain)}
                onOpenRecents={() => setDrawerOpen(true)}
                recent={recent}
                compact={hasReport || scanning}
                chain={chain}
                onChainChange={(c) => {
                  setChain(c);
                  setReport(null);
                  setError("");
                }}
              />
            </Card>
          </section>

          {error ? (
            <Card className="border-[hsl(var(--danger)/0.5)] bg-[hsl(var(--danger)/0.06)] mb-6">
              <CardContent className="py-3 text-sm text-[hsl(var(--danger))]">
                {error}
              </CardContent>
            </Card>
          ) : null}

          {scanning ? <ResultsSkeleton /> : null}

          {hasReport && !scanning ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6"
              data-testid="results-section"
            >
              <div className="lg:col-span-12">
                <TokenHeader report={report} />
              </div>

              {/* RWA signals — only for EVM, shown immediately after token header */}
              {report?.evm_signals ? (
                <div className="lg:col-span-12 space-y-3">
                  <RwaSignals evmSignals={report.evm_signals} />
                </div>
              ) : null}

              <div className="lg:col-span-4">
                <Card className="p-5">
                  <SafetyScoreGauge
                    score={report?.risk?.score ?? 0}
                    label={report?.risk?.label ?? "Danger"}
                    topHoldersPct={report?.risk?.top_holders_pct}
                  />
                  <Separator className="my-4" />
                  <div className="grid grid-cols-3 text-center gap-2">
                    <Mini stat="pass" checks={checks} />
                    <Mini stat="warn" checks={checks} />
                    <Mini stat="fail" checks={checks} />
                  </div>
                </Card>
              </div>

              <div className="lg:col-span-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {checks.map((c) => (
                    <RiskCheckCard key={c.id} check={c} />
                  ))}
                </div>
              </div>

              {report?.warnings?.length ? (
                <div className="lg:col-span-12">
                  <WarningsBanner warnings={report.warnings} />
                </div>
              ) : null}

              <div className="lg:col-span-12">
                <LiquidityPanel report={report} />
              </div>

              {/* HoneypotCard — only when data present */}
              {report?.honeypot ? (
                <div className="lg:col-span-12">
                  <HoneypotCard honeypot={report.honeypot} />
                </div>
              ) : null}

              {report?.volume_authenticity ? (
                <div className="lg:col-span-12">
                  <VolumeAuthenticityCard
                    volumeAuthenticity={report.volume_authenticity}
                  />
                </div>
              ) : null}

              {report?.risk_modifiers?.bcvs ? (
                <div className="lg:col-span-6">
                  <BcvsCard bcvs={report.risk_modifiers.bcvs} />
                </div>
              ) : null}
              {report?.risk_modifiers?.ceber ? (
                <div className="lg:col-span-6">
                  <CeberCard ceber={report.risk_modifiers.ceber} />
                </div>
              ) : null}
              {report?.risk_modifiers?.msis ? (
                <div className="lg:col-span-6">
                  <MsisCard
                    msis={report.risk_modifiers.msis}
                    risk={report.risk}
                  />
                </div>
              ) : null}
              {report?.risk_modifiers?.cmss ? (
                <div className="lg:col-span-6">
                  <CmssCard cmss={report.risk_modifiers.cmss} />
                </div>
              ) : null}

              {/* BundleAnalysisCard uses its own fetch; show for Solana only */}
              {isSolana ? (
                <div className="lg:col-span-12">
                  <BundleAnalysisCard mint={report.mint} />
                </div>
              ) : null}

              {/* HoldersTable — render when holders array is present */}
              {report?.holders?.length ? (
                <div className="lg:col-span-7">
                  <HoldersTable
                    holders={report.holders}
                    topPct={report?.risk?.top_holders_pct}
                  />
                </div>
              ) : null}

              {/* DeployerCard — render when deployer data is present */}
              {(report?.deployer || report?.deployer_history) ? (
                <div className={report?.holders?.length ? "lg:col-span-5" : "lg:col-span-12"}>
                  <DeployerCard report={report} />
                </div>
              ) : null}
            </motion.div>
          ) : null}

          {!hasReport && !scanning && !error ? (
            <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
              <BenefitCard
                icon={<ShieldCheck size={18} className="text-[hsl(var(--success))]" />}
                title="On-chain truth"
                text="Pulls authorities, supply, decimals, holders, and metadata directly from chain RPC."
              />
              <BenefitCard
                icon={<ShieldAlert size={18} className="text-[hsl(var(--warning))]" />}
                title="Liquidity & markets"
                text="Aggregates DEX pools via DexScreener for honest liquidity views across chains."
              />
              <BenefitCard
                icon={<ShieldX size={18} className="text-[hsl(var(--danger))]" />}
                title="Deployer scrutiny"
                text="Flags wallets that have deployed many tokens, plus mutable metadata risk."
              />
            </section>
          ) : null}
        </main>

        <Footer chain={chain} />
        <RecentScansDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          items={recent}
          onPick={(token, recentChain) => {
            setDrawerOpen(false);
            setChain(recentChain || "solana");
            handleScan(token, recentChain || "solana");
          }}
          onClear={handleClearRecent}
        />
        <Toaster />
      </div>
    </TooltipProvider>
  );
}

function Mini({ stat, checks }) {
  const count = checks.filter((c) => c.status === stat).length;
  const colors = {
    pass: "text-[hsl(var(--success))]",
    warn: "text-[hsl(var(--warning))]",
    fail: "text-[hsl(var(--danger))]",
  };
  const labels = { pass: "Pass", warn: "Warn", fail: "Fail" };
  return (
    <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] py-2">
      <div className={`text-xl font-semibold tabular-nums ${colors[stat]}`}>
        {count}
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))]">
        {labels[stat]}
      </div>
    </div>
  );
}

function BenefitCard({ icon, title, text }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h3 className="text-sm font-semibold tracking-wide">{title}</h3>
      </div>
      <p className="text-xs text-[hsl(var(--muted))] leading-relaxed">{text}</p>
    </Card>
  );
}
